<template>
  <div
    class="flex flex-col z-10"
    :class="wrapperClasses"
    data-test="review-reviewer-info"
  >
    <FormattedMessage
      class="heading-3 mb-20"
      :definition="translations.reviewerProfile"
      tag="p"
    >
      <template #userName>
        <span class="text-action-brand-hi">{{ userName }}</span>
      </template>
    </FormattedMessage>

    <div v-if="reviewerInfoPending" class="flex flex-col gap-2">
      <RevSkeleton class="h-20" height="20px" shape="rectangle" width="100%" />
      <RevSkeleton class="h-20" height="20px" shape="rectangle" width="100%" />
      <RevSkeleton class="h-20" height="20px" shape="rectangle" width="100%" />
    </div>

    <template v-else>
      <div class="body-2 text-static-default-low flex flex-col">
        <p v-if="reviewerInfo?.customerSince || countryCode" class="flex">
          <template v-if="reviewerInfo?.customerSince">
            {{
              i18n(translations.reviewerCustomerSince, {
                sinceDate: reviewerInfo.customerSince,
              })
            }}
          </template>

          <template v-if="reviewerInfo?.customerSince && countryCode">
            •
          </template>

          <template v-if="countryCode">
            {{ i18n.country(countryCode) }}
          </template>

          <Flag
            v-if="countryCode"
            aria-hidden="true"
            class="ml-4 inline-block align-baseline"
            :country-code="countryCode as MarketCountryCode"
          />
        </p>

        <p v-if="reviewerInfo?.purchaseCount">
          {{
            i18n(translations.reviewerPurchaseCount, {
              purchaseCount: reviewerInfo.purchaseCount,
            })
          }}
        </p>

        <p
          v-if="reviewerInfo?.reviewCount || reviewerInfo?.averageRating"
          class="flex items-center gap-4"
        >
          <template v-if="reviewerInfo?.reviewCount">
            {{
              i18n(translations.reviewerReviewCount, {
                reviewCount: reviewerInfo.reviewCount,
              })
            }}
          </template>

          <template
            v-if="reviewerInfo?.reviewCount && reviewerInfo?.averageRating"
          >
            •
          </template>

          <template v-if="reviewerInfo?.averageRating">
            {{
              i18n(translations.reviewerAverageRating, {
                averageRating: reviewerInfo?.averageRating,
              })
            }}
            <IconStarFilled class="size-14" />
          </template>
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { IconStarFilled } from '@backmarket/design-system/icons/IconStarFilled'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { RevSkeleton } from '@ds/components/Skeleton'

import type { ReviewerInfo } from '../../../api/reviews/types'

import translations from './ReviewerInfo.translations'

const i18n = useI18n()

const { countryCode, userName, infoType, reviewerInfo, reviewerInfoPending } =
  defineProps<{
    countryCode?: string
    reviewerInfo?: ReviewerInfo
    userName: string
    infoType: 'popover' | 'modal'
    reviewerInfoPending: boolean
  }>()

const wrapperClasses = computed(() => {
  if (infoType === 'modal') return ''

  return tw`bg-surface-default-low shadow-long rounded-lg py-24 px-40`
})
</script>
